import {
	differenceInMinutes,
	differenceInHours,
	differenceInDays,
	differenceInMonths,
	differenceInYears,
} from 'date-fns';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

const i18n = defineMessages({
	justNow: {
		id: 'time-ago.relative.just-now',
		defaultMessage: 'Just now',
		description: 'Label for a timestamp less than a minute ago',
	},
	minutesAgo: {
		id: 'time-ago.relative.minutes-ago',
		defaultMessage: '{minuteCount} {minuteCount, plural, one {minute} other {minutes}} ago',
		description: 'Label for a timestamp that is minutes ago',
	},
	hoursAgo: {
		id: 'time-ago.relative.hours-ago',
		defaultMessage: '{hourCount} {hourCount, plural, one {hour} other {hours}} ago',
		description: 'Label for a timestamp that is hours ago',
	},
	daysAgo: {
		id: 'time-ago.relative.days-ago',
		defaultMessage: '{dayCount} {dayCount, plural, one {day} other {days}} ago',
		description: 'Label for a timestamp that is days ago',
	},
	monthsAgo: {
		id: 'time-ago.relative.months-ago',
		defaultMessage: '{monthCount} {monthCount, plural, one {month} other {months}} ago',
		description: 'Label for a timestamp that is months ago',
	},
	yearsAgo: {
		id: 'time-ago.relative.years-ago',
		defaultMessage: '{yearCount} {yearCount, plural, one {year} other {years}} ago',
		description: 'Label for a timestamp that is years ago',
	},
});

/**
 * Returns a human-friendly relative time string for a given date.
 *
 * Unlike `formatTimeAgo` (which switches to an absolute date after 24 hours),
 * this function always returns a relative description — even for dates that are
 * days, months, or years in the past.
 *
 * Gradation:
 * - < 1 minute  → "Just now"
 * - < 1 hour    → "X minute(s) ago"
 * - < 1 day     → "X hour(s) ago"
 * - < 31 days   → "X day(s) ago"
 * - < 12 months → "X month(s) ago"
 * - >= 1 year   → "X year(s) ago"
 *
 */
export const formatRelativeTimeAgo = (
	date: Date | string | number,
	intl: IntlShape,
	now: Date = new Date(),
): string => {
	const dateObject = new Date(date);

	const minutesDiff = differenceInMinutes(now, dateObject);
	const hoursDiff = differenceInHours(now, dateObject);
	const daysDiff = differenceInDays(now, dateObject);
	const monthsDiff = differenceInMonths(now, dateObject);
	const yearsDiff = differenceInYears(now, dateObject);

	if (minutesDiff < 1) {
		return intl.formatMessage(i18n.justNow);
	}
	if (minutesDiff < 60) {
		return intl.formatMessage(i18n.minutesAgo, { minuteCount: minutesDiff });
	}
	if (hoursDiff < 24) {
		return intl.formatMessage(i18n.hoursAgo, { hourCount: hoursDiff });
	}
	if (daysDiff < 31) {
		return intl.formatMessage(i18n.daysAgo, { dayCount: daysDiff });
	}
	if (monthsDiff < 12) {
		return intl.formatMessage(i18n.monthsAgo, { monthCount: monthsDiff });
	}
	return intl.formatMessage(i18n.yearsAgo, { yearCount: yearsDiff });
};
